import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import type {
  GameDetail,
  InviteResponse,
  LobbyResponse,
  MatchmakingMode,
  PendingChallenge,
  VariantSummary,
  WsServerMessage,
} from '../api/types';
import { useApp } from '../app/AppContext';
import { config } from '../config';
import { useI18n } from '../i18n';
import type { StringKey } from '../i18n/strings';

const MODES: MatchmakingMode[] = ['random', 'invite', 'vs_ai', 'direct'];

// ---------------------------------------------------------------------------
// 純函式：線路訊息 → 這一頁該做的決定
//
// ★ 為什麼要把這幾格從 effect 裡拆出來 export：留在 effect 裡的邏輯很容易變成
//   「只有原始碼文字在守著」——而那正是這個 repo 已經記錄過的坑：
//   「接線寫了但寫錯」讀原始碼抓不到。拆成純函式之後，那幾條接縫可以用
//   **後端真的送出來的形狀**跑一次真的程式碼，而不是跑一份複製品。
//
// ⚠️ **這一節不是 effect 的替代品，是它的補集。** 2026-08-21 的對抗式覆核示範過
//   兩者的差別：把 effect 裡那一行改成 `if (invite && false) setRematchInvite(invite)`
//   （純函式一個字都沒動），全前端 600/600 仍然全綠。
//   所以 effect 那一半現在由 `lobbyRematchInvite.test.tsx` 用真的 React
//   ＋真的 WebSocket 蓋著（本 app 的 vitest 預設環境仍是 `node`，
//   需要 DOM 的測試自己宣告 `// @vitest-environment jsdom`）。
// ---------------------------------------------------------------------------

/**
 * 這則訊息是不是「我開的那張邀請桌沒了」（C02 §6.4 的鏡像缺口）。
 *
 * ## 為什麼需要它（本輪修復自己造出來的第一個缺口）
 *
 * 邀請人現在會訂閱 `match:<invite.match_id>`（為了收 `match_started`），
 * 所以後端 `abandon_no_show()` / 管理員作廢送出的 **`match_voided`
 * 真的會抵達他的連線** —— 位元有送到，只是修復前這一頁對它**零分支**：
 * 桌子死了，畫面上的邀請碼還亮著，朋友點下去拿到 404，而邀請人毫不知情。
 *
 * 這是本 repo 那個反覆出現的形狀的**鏡像**：先前八個接縫是
 * 「後端沒送、前端有消費者」，這一個是「**後端有送、前端沒有消費者**」，
 * 而兩邊的測試都是綠的。
 *
 * ## ★ 為什麼一定要比對 `match_id`
 *
 * 一條 WebSocket 同時載著三種頻道（`subject:` / `queue:` / `match:`），
 * 而 `onMessage` 是**整條連線**的回呼 —— 它收得到的不只我這張桌子的事件。
 * 不比對 id 的話，任何一則 `match_voided` 都會把玩家手上還活著的邀請碼清掉。
 * 「我收到一個作廢事件」與「**我的**桌子沒了」是兩件事，不可以共用一個判斷。
 */
export function isInviteTableGone(msg: WsServerMessage, inviteMatchId: string | null): boolean {
  if (!inviteMatchId) return false;
  if (msg.op !== 'event') return false;
  return String(msg.event_type) === 'match_voided' && msg.match_id === inviteMatchId;
}

/**
 * `challenge_received` 的 payload → {@link PendingChallenge}，**讀不懂就回 `null`**。
 *
 * ★ 四個欄位缺一不可，而且必須都是非空字串。理由與 `SeatInfo.kind` 那一條逐字相同
 *   （C02 §6.1 的通則）：**「我讀不到」與「真的有一筆挑戰」不得編碼成同一個值**。
 *   半筆挑戰渲染出來的樣子是「一個沒有名字的人挑戰你玩一個沒有名字的玩法」，
 *   而按下「接受」會送出一張 `opponent_subject_id: ""` 的票 —— 那張票永遠配不到，
 *   玩家看到的是「配對中… 已等待 118 秒」（後端測試逐字記過這個症狀）。
 *
 * 形狀取自後端 `lobby/service.py::_push_challenge()` 真正組出來的那個 dict，
 * **不是**照契約範例手抄的。
 */
export function readPendingChallenge(payload: unknown): PendingChallenge | null {
  if (typeof payload !== 'object' || payload === null) return null;
  const p = payload as Record<string, unknown>;
  const str = (k: string): string | null => (typeof p[k] === 'string' && p[k] ? (p[k] as string) : null);
  const from_subject_id = str('from_subject_id');
  const game_key = str('game_key');
  const variant_key = str('variant_key');
  if (!from_subject_id || !game_key || !variant_key) return null;
  return {
    from_subject_id,
    // ★ 只有暱稱可以缺：後端 `principal.display_name` 對某些身分是空字串，
    //   而「不知道他叫什麼」不該讓整筆挑戰消失 —— 清單本來就會退回顯示 subject id。
    from_display_name: str('from_display_name') ?? '',
    game_key,
    variant_key,
  };
}

// ---------------------------------------------------------------------------
// ★ 再戰邀請（C02 §5 v1.7 的 `rematch_requested`，走 §6.4 的個人頻道）
// ---------------------------------------------------------------------------

/**
 * 「有人想跟我再戰」——`rematch_requested` 在大廳頁的投影。
 *
 * ## 為什麼大廳也要有這一段（不是只有對局頁）
 *
 * 對手按下再戰的那一刻，我很可能**已經離開結束畫面回到大廳**了
 * （那正是最自然的動線：打完 → 回大廳 → 找下一場）。個人頻道在大廳是**永遠**訂著的，
 * 而 C02 §6.4 又強制 `rematch_requested` 納入「訂閱即回放」——
 * 也就是說契約已經保證這則事件會抵達這一頁，缺的只是一個消費者。
 * 少了它，我在大廳重新整理一次就再也不知道有人在等我，
 * 而那正是 v1.7 那條「後端有送、前端沒有消費者」的鏡像缺口。
 */
export interface RematchInvite {
  /** **原對局**的 id（信封的 `match_id`）—— 回到那一頁就能按下接受。 */
  matchId: string;
  fromDisplayName: string;
  variantKey: string;
  /** 意願鑰匙的到期**時刻**（ISO-8601）。讀不到就是 `null` ⇒ 不顯示倒數。 */
  expiresAt: string | null;
}

/**
 * `rematch_requested` → {@link RematchInvite}，**讀不懂就回 `null`**。
 *
 * ★ 唯一的必要欄位是信封的 `match_id`：沒有它就**沒有地方可以去**，
 *   而一顆按下去不知道要導向哪裡的按鈕比沒有按鈕更糟
 *   （與 `readPendingChallenge()` 對 `from_subject_id` 的態度逐字相同）。
 *   暱稱／玩法缺席只是少幾個字，畫面會退回講「對手」。
 *
 * ⚠️ 這個事件的 `match_id` 是**原對局**的 id，**不是**新對局 ——
 *   新對局在這一刻還不存在（那正是 v1.7 要新增這個事件的原因）。
 *   所以它只能拿來**回到結束畫面**，不能當成「有一場新對局在等我」。
 */
export function readRematchInvite(msg: WsServerMessage): RematchInvite | null {
  if (msg.op !== 'event') return null;
  if (String(msg.event_type) !== 'rematch_requested') return null;
  if (typeof msg.match_id !== 'string' || msg.match_id === '') return null;
  const p = (msg.payload ?? {}) as Record<string, unknown>;
  const str = (k: string): string => (typeof p[k] === 'string' ? (p[k] as string) : '');
  const expiresAt = str('expires_at');
  return {
    matchId: msg.match_id,
    fromDisplayName: str('from_display_name'),
    variantKey: str('variant_key'),
    expiresAt: expiresAt !== '' && Number.isFinite(Date.parse(expiresAt)) ? expiresAt : null,
  };
}

/**
 * 這則訊息是不是「剛才那份再戰邀請被撤回了」（`rematch_cancelled`）。
 *
 * ★ 必須比對 `match_id`，理由與 {@link isInviteTableGone} 逐字相同：
 *   一條 WebSocket 同時載著三種頻道，`onMessage` 是**整條連線**的回呼。
 *   「我收到一則撤回」與「**我畫面上那一份**被撤回了」是兩件事。
 *
 * ⚠️ 契約明文：`rematch_cancelled` **不做回放**。所以「沒收到撤回」
 *   不代表邀請還活著 —— 邀請的死活只由 `expires_at` 決定（見 `rematchInviteLive()`）。
 */
export function isRematchInviteWithdrawn(msg: WsServerMessage, inviteMatchId: string | null): boolean {
  if (!inviteMatchId) return false;
  if (msg.op !== 'event') return false;
  return String(msg.event_type) === 'rematch_cancelled' && msg.match_id === inviteMatchId;
}

/**
 * 這份邀請這一刻還活著嗎。
 *
 * `expiresAt` 為 `null`（事件沒帶得出可用的絕對時刻）時**視為還活著** ——
 * fail-open 的理由見 `machine.ts` 的 `INCOMING_REMATCH_NO_EXPIRY_MESSAGE`：
 * 少顯示的代價是玩家完全不知道有人在等他，那正是這一輪在修的東西。
 * 前端**不得**自己挑一個秒數補上去。
 */
export function rematchInviteLive(invite: RematchInvite | null, now: number): boolean {
  if (!invite) return false;
  if (invite.expiresAt === null) return true;
  const at = Date.parse(invite.expiresAt);
  return !Number.isFinite(at) || at > now;
}

/** 一筆挑戰的身分（收件匣是 list，同一個人可以疊很多筆）。 */
function challengeIdentity(c: PendingChallenge): string {
  return `${c.from_subject_id}\0${c.game_key}\0${c.variant_key}`;
}

/**
 * 大廳回應裡的挑戰 ∪ 個人頻道上收到的挑戰，**去重**。
 *
 * ## 為什麼兩個來源都要，而不是只留一個
 *
 * | 來源 | 蓋得住的情境 | 蓋不住的 |
 * | :-- | :-- | :-- |
 * | `GET /lobby/{game}.pending_challenges` | 進大廳前就已經存在的挑戰 | 我在大廳站著時**新到**的（最多等 8 秒的輪詢） |
 * | `challenge_received`（個人頻道） | 此刻新到的、以及 C02 §6.4「訂閱即回放」補送的 | 我**還沒訂閱**時就到的（Redis pub/sub 沒有回溯） |
 *
 * 兩者互為對方的補集，所以是聯集而不是二選一。
 *
 * ## ★ 去重是**契約要求**的
 *
 * C02 §6.4：「補送的事件必須與 publish 路徑同形狀，**客戶端本來就要能容忍重複**（冪等）」。
 * 一次閃斷就會讓同一筆挑戰同時出現在回放與大廳回應裡；
 * 而後端實測「同一個人取消再挑戰一次，收件匣裡就會有**內容逐字相同**的兩筆」
 * （`test_the_same_challenger_can_stack_identical_entries_in_one_inbox`）——
 * 那兩筆用 `from_subject_id + variant_key` 當 React key 會**兄弟節點撞 key**。
 * 在這裡收斂，畫面那一層就永遠拿得到一份 key 唯一的清單。
 *
 * 順序：大廳回應（權威、可回溯）在前，事件補進來的在後 —— 新到的挑戰排在下面，
 * 不會讓已經在螢幕上的那幾筆跳動。
 */
export function mergePendingChallenges(
  fromLobby: readonly PendingChallenge[],
  fromEvents: readonly PendingChallenge[],
): PendingChallenge[] {
  const out: PendingChallenge[] = [];
  const seen = new Set<string>();
  for (const c of [...fromLobby, ...fromEvents]) {
    const key = challengeIdentity(c);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

// ---------------------------------------------------------------------------
// ★★ 個人頻道 `subject:<subject_id>` 的分派（C02 §6.4）—— 一則訊息 → 這一頁的決定
// ---------------------------------------------------------------------------

/**
 * 一則個人頻道訊息造成的**全部**後果。四格互不重疊，缺一不可。
 *
 * ★ 為什麼要有這個型別：底下那個 effect 以前是一串 `if / return` 直接呼叫
 *   `setRematchInvite()` / `refreshLobby()` / `goToMatch()` —— 分派邏輯與副作用
 *   焊死在一起，而 effect **在 `node` 環境下跑不起來**，於是唯一守著它的東西是
 *   `lobbyPage.test.ts` 的原始碼**文字**掃描。2026-08-21 的對抗式覆核量給我們看那有多空：
 *
 *   > 把 `if (invite) setRematchInvite(invite)` 改成 `if (invite && false) …`
 *   > （分支文字保留、行為癱瘓），前端 **600/600 全綠**。
 *
 *   把「決定」抽成純函式之後，那條分派可以用**後端真的送出來的形狀**跑一次真的程式碼；
 *   而 effect 只剩「照著決定去做」——它本身則由 `lobbyRematchInvite.test.tsx`
 *   用真的 React ＋ 真的 WebSocket 蓋住。
 */
export interface SubjectEventDecision {
  /**
   * 這則訊息之後，畫面上那份再戰邀請該變成什麼。
   * **是「下一個值」，不是「新到的邀請」**——沒有變化時它就是傳進來的 `prev` 本人。
   */
  rematchInvite: RematchInvite | null;
  /** 這則訊息帶來的新挑戰（`null` = 沒有，或讀不懂 ⇒ 當作沒有）。 */
  challenge: PendingChallenge | null;
  /** 要不要重讀一次大廳（`GET /lobby/{game_key}`）。 */
  reloadLobby: boolean;
  /** 要把玩家帶去哪一場（`null` = **不導頁**）。 */
  navigateTo: string | null;
}

/**
 * 個人頻道上的一則訊息 → 這一頁該做的決定（**純函式，零副作用**）。
 *
 * | 事件 | 何時 | 這裡的決定 |
 * | :-- | :-- | :-- |
 * | `rematch_requested` | 對手按了再戰、正在等我（C02 §5 v1.7） | 記下邀請，**不導頁** |
 * | `rematch_cancelled` | 他把意願撤回了 | 只有 `match_id` 對得上才清掉 |
 * | `challenge_received` | 有人指名挑戰我 | 收下那筆挑戰 ＋ 重讀大廳 |
 * | `seat_joined` / `match_created` / `match_started` | 我的局有著落了 | 導進那場對局 |
 *
 * ⚠️ **`rematch_requested` 絕對不可以落進導頁那一格。** 它的 `match_id` 是**原對局**的 id，
 *   新對局此刻還不存在 —— 自動導頁等於把玩家丟回一場已經結束的對局。
 *   這與 `challenge_received` 的 `match_id` 是 `null` 是同一條規則的兩種寫法：
 *   **事件裡的 id 是什麼，決定它能拿來做什麼。**
 *
 * ⚠️ `challenge_received` 用字串比對而不是型別窄化：它是**服務層**事件
 *   （`Hub.publish_raw()`），三份跨語言副本的一致性由 `response-contract.test.ts`
 *   三方比對守著，這裡不重複那份判斷。
 *
 * 冪等：續期會重送、訂閱即回放也會補送一則等價事件（契約明文），
 * 所以 `rematch_requested` 是**覆蓋同一格**，不是往清單裡加一筆。
 *
 * @param prev 目前畫面上那份邀請 —— `rematch_cancelled` 必須拿它的 `match_id` 比對，
 *             否則**別場**對局的撤回會把我手上這一份清掉（一條 WS 同時載著三種頻道）。
 */
export function applySubjectEvent(msg: WsServerMessage, prev: RematchInvite | null): SubjectEventDecision {
  const unchanged: SubjectEventDecision = {
    rematchInvite: prev,
    challenge: null,
    reloadLobby: false,
    navigateTo: null,
  };
  if (msg.op !== 'event') return unchanged;
  const eventType = String(msg.event_type);
  if (eventType === 'rematch_requested') {
    const invite = readRematchInvite(msg);
    return invite ? { ...unchanged, rematchInvite: invite } : unchanged;
  }
  if (eventType === 'rematch_cancelled') {
    return isRematchInviteWithdrawn(msg, prev?.matchId ?? null)
      ? { ...unchanged, rematchInvite: null }
      : unchanged;
  }
  if (eventType === 'challenge_received') {
    // `refreshLobby()` 照打：事件與大廳回應互為補集，聯集與去重在 `mergePendingChallenges()`。
    return { ...unchanged, challenge: readPendingChallenge(msg.payload), reloadLobby: true };
  }
  if (eventType === 'seat_joined' || eventType === 'match_created' || eventType === 'match_started') {
    return { ...unchanged, navigateTo: msg.match_id ? msg.match_id : null };
  }
  return unchanged;
}

function variantLabelKey(variantKey: string): StringKey | null {
  switch (variantKey) {
    case 'rps-bo1':
      return 'lobby.variant.rps-bo1';
    case 'rps-bo3':
      return 'lobby.variant.rps-bo3';
    case 'rps-bo5':
      return 'lobby.variant.rps-bo5';
    case 'rps-first-to-5':
      return 'lobby.variant.rps-first-to-5';
    default:
      return null;
  }
}

export function LobbyPage() {
  const { t } = useI18n();
  const { api, session, socket, guestState, guestError, retryGuest, onMessage } = useApp();
  const navigate = useNavigate();

  const [game, setGame] = useState<GameDetail | null>(null);
  const [lobby, setLobby] = useState<LobbyResponse | null>(null);
  const [variantKey, setVariantKey] = useState<string | null>(null);
  const [mode, setMode] = useState<MatchmakingMode>('random');
  /**
   * ★ C02 §4.1：`ai_profile` 的值域**由伺服器給**，客戶端不得寫死。
   *
   * 修復前這裡是 `useState('nash')` —— 那才是玩家不動選單時**真的會送出去**的值，
   * 而後端只認得 `nash-uniform`，所以「挑戰 AI」100% 回 422。
   * （只改 `<option value>` 不夠：預設值是另一條獨立的路徑。）
   *
   * `null` 的意思是**還沒有合法的值可以送** —— 名單還沒載入，或伺服器沒開放 AI。
   * 這種時候正確的行為是把 vs_ai 停用，不是送一個猜的字串上去。
   */
  const [aiProfile, setAiProfile] = useState<string | null>(null);
  const [opponentId, setOpponentId] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [invite, setInvite] = useState<InviteResponse | null>(null);
  const [copied, setCopied] = useState(false);
  /**
   * ★ C02 §6.4：個人頻道上收到的挑戰（`challenge_received` 的 payload 本體）。
   *
   * 修復前這一頁對 `challenge_received` 的處理只有 `refreshLobby()` ——
   * 也就是**把事件當成一顆按鈕按下去，內容整份丟掉**，再打一次 HTTP 去問同一件事。
   * 那條路徑在多數情況下會動，但它把兩件事綁在一起：
   * 「有人挑戰我」變得**取決於那次 `GET /lobby` 有沒有成功**
   * （而它的 catch 是 `.catch(() => undefined)`，失敗完全無聲），
   * 於是 C02 §6.4 花了一整節立起來的個人頻道，在最後一公尺又退回輪詢。
   *
   * 事件自帶完整的挑戰內容（後端 `_push_challenge()` 收件匣與事件**共用同一個 dict**），
   * 所以這裡把它直接收下來，與大廳回應**聯集**後渲染。見 {@link mergePendingChallenges}。
   */
  const [liveChallenges, setLiveChallenges] = useState<PendingChallenge[]>([]);
  /**
   * ★ C02 §5 v1.7：對手按了再戰、正在等我（`rematch_requested`，走個人頻道）。
   *
   * **只留最新的一份。** 一個人同時被兩場舊對局邀請是理論上可能、實務上罕見的，
   * 而「一份邀請」比「一份清單」少一整套去重／排序／過期的路徑；
   * 重複事件（續期重送、訂閱即回放）在這裡自然就是冪等的 —— 覆蓋同一格。
   */
  const [rematchInvite, setRematchInviteState] = useState<RematchInvite | null>(null);
  /**
   * ★ 目前那份邀請的**同步**鏡像 —— `applySubjectEvent()` 需要 `prev` 才決定得了
   *   「這則 `rematch_cancelled` 撤的是不是我手上這一份」。
   *
   * ⚠️ 這個 ref 只能經由底下的 {@link setRematchInvite} 寫入。直接呼叫
   *   `setRematchInviteState()` 會讓 ref 與 state 分岔，而分岔的症狀是
   *   「撤回事件偶爾沒反應」——一個安靜、間歇、找不到的錯。
   *
   * 為什麼不用 `setRematchInvite(prev => …)` 的 functional update 取得 `prev`：
   * updater 必須是**純的**（StrictMode 會重跑它），而我們要在同一次分派裡
   * 順便決定導頁與挑戰。ref 是同步的，所以它只會比 state **更新**，不會更舊。
   */
  const rematchInviteRef = useRef<RematchInvite | null>(null);
  const setRematchInvite = useCallback((next: RematchInvite | null) => {
    rematchInviteRef.current = next;
    setRematchInviteState(next);
  }, []);
  /** 邀請倒數用的心跳。只有邀請存在時才跳（見底下那個 effect）。 */
  const [inviteNow, setInviteNow] = useState(() => Date.now());
  const [ticketId, setTicketId] = useState<string | null>(null);
  const [queueStartedAt, setQueueStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  /** 非錯誤但必須說出口的事（排隊逾時／取消）。見 `strings.ts` 的 `lobby.queueExpired`。 */
  const [notice, setNotice] = useState<StringKey | null>(null);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * ★★ 導頁去重（C02 §6.4「訂閱即回放」的必要配套）。
   *
   * 後端在 `subscribe()` 之後會**立刻重讀當下狀態並補送等價事件**，
   * 而同一個 `match_created` 也會同時出現在 `queue:<ticket_id>` 與
   * `subject:<subject_id>` 兩條頻道上（契約明說那是鏡像）。
   * 也就是說**收到重複事件是正常的、是刻意的**，客戶端本來就必須冪等。
   * 這個 ref 記住「已經導往哪一場」，讓同一個目標只導一次。
   */
  const navigatedRef = useRef<string | null>(null);

  // --- 載入目錄 + 大廳狀態 ------------------------------------------------
  useEffect(() => {
    const ac = new AbortController();
    api
      .getGame(config.gameKey, ac.signal)
      .then((g) => {
        setGame(g);
        setVariantKey((prev) => prev ?? g.variants.find((v) => v.published)?.variant_key ?? null);
      })
      .catch(() => {
        /* 目錄拿不到不致命：大廳仍可用邀請碼加入 */
      });
    return () => ac.abort();
  }, [api]);

  const refreshLobby = useCallback(() => {
    api
      .getLobby(config.gameKey)
      .then((l) => setLobby(l))
      .catch(() => undefined);
  }, [api]);

  useEffect(() => {
    if (guestState !== 'ready') return;
    refreshLobby();
    const id = setInterval(refreshLobby, 8000);
    return () => clearInterval(id);
  }, [guestState, refreshLobby]);

  // --- 排隊：WS 是主通道，輪詢是降級路徑（C02 §4） -------------------------
  const goToMatch = useCallback(
    (matchId: string) => {
      // ★ 冪等：同一個目標只導一次（見 navigatedRef 的說明）。
      if (navigatedRef.current === matchId) return;
      navigatedRef.current = matchId;
      setTicketId(null);
      setQueueStartedAt(null);
      navigate(`/m/${encodeURIComponent(matchId)}`);
    },
    [navigate],
  );

  /**
   * ★ C02 §4.2：`my_ticket` 讓「排隊中」變成**可回復**的狀態。
   *
   * 修復前排隊狀態只活在 `useState` 裡：重新整理或換分頁之後前端就忘了自己在排隊，
   * 而票券還在 Redis 上活著（而且同一個 subject 只能有一張，再按會撞 409）。
   *
   * ⚠️ **只認 `queued`，不對 `matched` 自動導頁。** 後端的
   *   `active_ticket_id_for()` 讀的是一把還沒到期的 Redis key，
   *   它**會**回一張已經 `matched` 的舊票（`TicketStore.enqueue()` 自己就處理過
   *   「已經是終態但還沒清」這個狀況）。照著它導頁，玩家一回大廳就會被彈回
   *   一場可能早就結束的對局。「回到進行中的對局」由 `my_active_match`
   *   那顆按鈕負責 —— 那是玩家自己按的，而且後端只回還沒結束的那一場。
   */
  useEffect(() => {
    const mine = lobby?.my_ticket;
    if (!mine || mine.status !== 'queued') return;
    setTicketId((prev) => prev ?? mine.ticket_id);
    setQueueStartedAt((prev) => prev ?? Date.now());
  }, [lobby]);

  /**
   * ★★ C02 §6.4：個人頻道 `subject:<subject_id>` —— **永遠**訂閱。
   *
   * 平台原本只有 `match:<id>`（一場對局的參與者）與 `queue:<ticket_id>`（一張票），
   * **沒有任何頻道代表「一個人」**。而「在大廳等待的人」正是一個沒有對局、
   * 也不一定有票的狀態：產完邀請碼等朋友、被指名挑戰 —— 兩種情況他都不在任何頻道上，
   * 所以邀請碼走不完、指名對決走不完、媒合只能靠輪詢**是同一個缺口的三個症狀**。
   *
   * 只能訂閱**自己的** subject_id；訂別人的後端回 `NOT_FOUND`（不是 FORBIDDEN ——
   * 可區分的錯誤碼等於一個帳號存在性探測器）。
   */
  useEffect(() => {
    const subjectId = session?.subjectId;
    if (!socket || !subjectId) return;
    socket.subscribe(`subject:${subjectId}`);
    return () => socket.unsubscribe(`subject:${subjectId}`);
  }, [socket, session?.subjectId]);

  /**
   * ★★ 排隊時訂閱 `queue:<ticket_id>` —— 媒合成功的**主通道**（C02 §4 v1.1）。
   *
   * 修復前全前端唯一的 subscribe 呼叫點是 `useMatch.ts` 的 `match:`，
   * 於是 `matchmaker.py` publish 到 `queue:` 的 `match_created` 沒有任何人在聽，
   * 媒合全靠下面那個 1.5 秒輪詢在撐 —— 而 C02 §4 明說輪詢是**降級路徑**。
   * 代價不只是慢：`ready_deadline_at` 只有 30 秒，Chrome 會節流隱藏分頁的 timer，
   * 而「按下排隊之後切去別的分頁等」正是這個功能最典型的用法；
   * 遲到的一方會被 `abandon_no_show()` 記一場敗仗。WS 訊息不受 timer 節流影響。
   */
  useEffect(() => {
    if (!socket || !ticketId) return;
    socket.subscribe(`queue:${ticketId}`);
    return () => socket.unsubscribe(`queue:${ticketId}`);
  }, [socket, ticketId]);

  /**
   * ★★ 產生邀請碼之後訂閱那一場對局（C02 §4 的 `match_id` + §6.3）。
   *
   * 修復前建立邀請碼之後頁面完全靜止：不排隊、不訂閱、也沒有以 `invite` 為依賴的
   * effect，所以**朋友到了邀請人也不知道**。
   *
   * 這條訂閱同時做了兩件事：
   *   ① 朋友加入 ⇒ 開局 ⇒ `match_started` 沿著這個頻道送到邀請人手上；
   *   ② 滿足 C02 §6.3 的開局條件（每個人類座位都必須**曾經**訂閱過一次）——
   *      邀請人若從來沒訂閱過，那一場會停在 `ready` 直到 reaper 判他棄權。
   */
  useEffect(() => {
    const inviteMatchId = invite?.match_id;
    if (!socket || !inviteMatchId) return;
    socket.subscribe(`match:${inviteMatchId}`);
    return () => {
      // 已經被帶進這一場的話就不要退訂 —— 對局頁馬上要用同一個頻道。
      if (navigatedRef.current === inviteMatchId) return;
      socket.unsubscribe(`match:${inviteMatchId}`);
    };
  }, [socket, invite]);

  /**
   * ★ 個人頻道上的事件（C02 §6.4）—— **只搬運，不判斷**。
   *
   * 「哪一則事件造成哪一種後果」整段住在 {@link applySubjectEvent}（純函式，
   * 上面那一節），這裡只負責把它回傳的四格套到 React 上。
   *
   * ★★ **不准在這裡長出第二份判斷。** 那會把接縫從「沒有人測」變成
   *   「測的不是跑的那一份」——後者更糟，因為它看起來是綠的。
   *   要改分派規則就改 `applySubjectEvent()`，那裡有 `lobbyPage.test.ts` 的
   *   逐欄測試；要改「套用的方式」才改這裡，那由 `lobbyRematchInvite.test.tsx`
   *   用真的 React ＋ 真的 WebSocket 蓋著。
   */
  useEffect(() => {
    return onMessage((msg) => {
      const {
        rematchInvite: invite,
        challenge,
        reloadLobby,
        navigateTo,
      } = applySubjectEvent(msg, rematchInviteRef.current);
      setRematchInvite(invite);
      if (challenge) setLiveChallenges((prev) => mergePendingChallenges(prev, [challenge]));
      if (reloadLobby) refreshLobby();
      if (navigateTo) goToMatch(navigateTo);
    });
  }, [onMessage, goToMatch, refreshLobby, setRematchInvite]);

  /**
   * ★★ 我開的那張邀請桌被收掉了（C02 §6.4 的鏡像缺口，2026-08-19 修復）。
   *
   * 後端在兩種情況下把 `match_voided` publish 到 `match:<id>`：
   *   * `abandon_no_show()` —— `ready_deadline_at` 到期（邀請局現在對齊
   *     `lobby_invite_ttl_s`，C02 §4.3 v1.6）；
   *   * 管理員 `POST …/void`。
   * 而邀請人**正訂著那條頻道**（上面那個 effect），所以位元早就送到了他的連線上，
   * 修復前只是沒有人接 —— 邀請碼還亮在畫面上，朋友點下去拿到 404。
   *
   * 做三件事，缺一不可：
   *   ① 把邀請碼狀態清掉 —— 它已經沒有對局可以加入了，留著就是在說謊；
   *   ② 用一句**字典裡的話**告訴他發生什麼（`invite.tableGone`），
   *      而不是讓畫面靜靜地變空；
   *   ③ 把模式切回 `invite`，讓他底下那顆主按鈕**當場就是**「產生邀請碼」——
   *      「可以重新產生一個」必須是一個他看得到、按得到的動作，
   *      不是「理論上他可以自己再找一次」。
   *
   * ★ 這個 effect 的**擺放位置不再有任何限制**（2026-08-21）。
   *
   *   以前這裡寫著「刻意排在訂閱 effect 之後」，因為 `lobbyInviteRecheck.test.ts`
   *   的解析器用 `indexOf('invite?.match_id')` 找訂閱 effect（取第一個命中），
   *   而這一段也寫著同一個運算式 —— 也就是說**測試的解析方式變成了
   *   產品原始碼的排版限制**，而唯一守著它的東西是這句註解本身。
   *
   *   那條錨點已經收緊：解析器改用只屬於訂閱 effect 的守衛當地標，
   *   而且地標命中兩個以上不同的 `useEffect` 時會**當場 throw**，不再默默取第一個。
   *   所以現在重排 effect 是安全的，而「解析器切錯」也不會再長得像「缺陷修好」。
   *
   * ⚠️ 但**不要在本檔的註解裡逐字抄那個地標**：解析器讀的是整份原始碼，
   *   註解裡的一份複本就是第二個命中，它會讓那個「地標唯一」的檢查當場變紅。
   *   （這一句是實測踩到的，不是推論。）
   */
  useEffect(() => {
    const inviteMatchId = invite?.match_id ?? null;
    if (!inviteMatchId) return;
    return onMessage((msg) => {
      if (!isInviteTableGone(msg, inviteMatchId)) return;
      setInvite(null);
      setCopied(false);
      setNotice('invite.tableGone');
      setMode('invite');
    });
  }, [onMessage, invite]);

  /**
   * ★ 再戰邀請的倒數心跳（C02 §5 v1.7）。
   *
   * 只在**有邀請而且它帶得出到期時刻**時才跳；到期由 `rematchInviteLive()` 判斷，
   * 而它讀的是伺服器給的**絕對時刻** —— 前端不從「收到事件那一刻」起算一個時長
   * （C02 §6.2 已經裁決過那個錯：客戶端無法從時長還原期限）。
   *
   * 依賴取 `expiresAt` 這個**字串**而不是整個物件：續期／回放會送來內容相同的新物件，
   * 用物件當依賴會讓計時器被反覆重建 —— 那是「冪等」在 effect 層的樣子。
   */
  useEffect(() => {
    const expiresAt = rematchInvite?.expiresAt ?? null;
    if (!expiresAt) return;
    const id = setInterval(() => setInviteNow(Date.now()), 500);
    return () => clearInterval(id);
  }, [rematchInvite?.expiresAt]);

  useEffect(() => {
    if (!ticketId) return;
    return onMessage((msg) => {
      if (msg.op !== 'event') return;
      // 媒合成功的事件形狀：`match_created` 帶 match_id。
      if (msg.event_type === 'match_created' || msg.event_type === 'match_started') {
        if (msg.match_id) goToMatch(msg.match_id);
      }
    });
  }, [ticketId, onMessage, goToMatch]);

  useEffect(() => {
    if (!ticketId) {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
      return;
    }
    pollRef.current = setInterval(() => {
      api
        .getTicket(ticketId)
        .then((s) => {
          if (s.status === 'matched' && s.match_id) goToMatch(s.match_id);
          if (s.status === 'expired' || s.status === 'cancelled') {
            setTicketId(null);
            setQueueStartedAt(null);
            setNotice(s.status === 'expired' ? 'lobby.queueExpired' : 'lobby.queueCancelled');
          }
        })
        .catch((e: unknown) => {
          /*
           * ★ 這個 catch 修復前是一個「無條件回 undefined」的空 handler ——
           * **每一種**失敗都被吞掉。但那裡有兩種語意完全不同的失敗：
           *   * 暫時性網路錯誤 → 應該重試（下一個 tick 會再問一次）；
           *   * `404` → 票券已經被 Redis 清掉（TTL 到期），它**永遠不會**再回來。
           * 第二種被當成第一種的後果是 `ticketId` 不會被清，
           * 畫面永遠停在「配對中… 已等待 N 秒」，而且沒有任何訊息。
           * 那正是這個 repo 反覆犯的「我讀不到」與「真的沒有」共用一個值。
           */
          if (e instanceof ApiError && e.status === 404) {
            setTicketId(null);
            setQueueStartedAt(null);
            setNotice('lobby.queueExpired');
          }
        });
    }, 1500);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [ticketId, api, goToMatch]);

  useEffect(() => {
    if (!queueStartedAt) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - queueStartedAt) / 1000)), 500);
    return () => clearInterval(id);
  }, [queueStartedAt]);

  // --- AI 對手名單（C02 §4.1：值域由伺服器給）------------------------------
  /** `playable === false` 的不得提供給玩家選（契約明文）。缺席視為可玩。 */
  const aiProfiles = useMemo(
    () => (lobby?.ai_profiles ?? []).filter((p) => !!p.name && p.playable !== false),
    [lobby],
  );
  const vsAiAvailable = aiProfiles.length > 0;

  useEffect(() => {
    // 名單空的（沒載入 / 伺服器沒開放）⇒ 維持 null ⇒ vs_ai 不可選。
    setAiProfile((prev) => {
      if (aiProfiles.length === 0) return null;
      if (prev && aiProfiles.some((p) => p.name === prev)) return prev;
      return aiProfiles[0]!.name;
    });
  }, [aiProfiles]);

  // --- 動作 ---------------------------------------------------------------
  const startQueue = useCallback(async () => {
    if (!variantKey) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (mode === 'invite') {
        const resp = await api.createInvite(config.gameKey, variantKey);
        setInvite(resp);
        setCopied(false);
        return;
      }
      const ticket = await api.enqueue(config.gameKey, {
        variant_key: variantKey,
        mode,
        opponent_subject_id: mode === 'direct' ? opponentId.trim() || null : null,
        ai_profile: mode === 'vs_ai' ? aiProfile : null,
      });
      setTicketId(ticket.ticket_id);
      setQueueStartedAt(Date.now());
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setBusy(false);
    }
  }, [api, variantKey, mode, opponentId, aiProfile, t]);

  /**
   * ★ 接受一個指名挑戰（C02 §4.2）。
   *
   * `matchmaker.py` 要求**雙方互相指名**才配對，所以「接受」不是一個新的端點，
   * 而是**用 `mode:'direct'` 排一張反向的票**：對手是挑戰者、玩法用他指定的那個。
   *
   * ⚠️ 這裡刻意再寫一次 `api.enqueue(...)` 的物件字面量而不是共用一個 helper：
   *   後端 `QueueRequestBody` 是 `extra="forbid"`（多送一把 key 就整個 422），
   *   所以「這條路真的送出去的是哪幾個 key」必須在呼叫點看得見。
   */
  const acceptChallenge = useCallback(
    async (challenge: PendingChallenge) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const ticket = await api.enqueue(config.gameKey, {
          variant_key: challenge.variant_key,
          mode: 'direct',
          opponent_subject_id: challenge.from_subject_id,
          ai_profile: null,
        });
        setTicketId(ticket.ticket_id);
        setQueueStartedAt(Date.now());
      } catch (e) {
        setError(describeError(e, t));
      } finally {
        setBusy(false);
      }
    },
    [api, t],
  );

  const cancelQueue = useCallback(async () => {
    if (!ticketId) return;
    try {
      await api.cancelQueue(ticketId);
    } catch {
      /* 取消失敗就讓它自然過期 */
    }
    setTicketId(null);
    setQueueStartedAt(null);
  }, [api, ticketId]);

  const joinByCode = useCallback(async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await api.joinByCode(code);
      goToMatch(resp.match_id);
    } catch (e) {
      setError(describeError(e, t));
    } finally {
      setBusy(false);
    }
  }, [api, joinCode, goToMatch, t]);

  const variants = useMemo(() => (game?.variants ?? []).filter((v) => v.published), [game]);
  const queuedFor = (v: VariantSummary) => lobby?.variants.find((x) => x.variant_key === v.variant_key);
  /**
   * ★ C02 §4.2 ＋ §6.4：兩個來源的**聯集**，去重。
   *
   * `lobby.pending_challenges` 蓋「進大廳前就存在的」（而那正是後端補
   * 「訂閱即回放」要解決的情境），`liveChallenges` 蓋「我站在大廳時新到的」
   * 與回放補送的那幾筆。細節見 {@link mergePendingChallenges}。
   */
  const challenges = useMemo(
    () => mergePendingChallenges(lobby?.pending_challenges ?? [], liveChallenges),
    [lobby, liveChallenges],
  );

  /**
   * ★ 這一刻該不該在大廳畫出「有人想跟你再戰」（C02 §5 v1.7）。
   *
   * 到期就消失 —— 而「到期了沒」只從伺服器給的 `expires_at` 減出來。
   * `inviteNow` 只是讓這個 memo 隨心跳重算，它**不是**期限的來源。
   */
  const liveRematchInvite = useMemo(
    () => (rematchInviteLive(rematchInvite, inviteNow) ? rematchInvite : null),
    [rematchInvite, inviteNow],
  );
  const rematchInviteSeconds =
    liveRematchInvite?.expiresAt == null
      ? null
      : Math.max(0, Math.ceil((Date.parse(liveRematchInvite.expiresAt) - inviteNow) / 1000));

  // --- 匿名身分還沒好 -----------------------------------------------------
  if (guestState === 'creating' || guestState === 'idle') {
    return <p className="muted">{t('guest.creating')}</p>;
  }
  if (guestState === 'failed') {
    return (
      <div className="notice notice--error stack stack--tight">
        <strong>{t('guest.failed')}</strong>
        <span className="faint">{guestError}</span>
        <button type="button" className="btn btn--sm" onClick={retryGuest}>
          {t('guest.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="stack">
      {lobby?.my_active_match ? (
        <div className="notice spread">
          <span>{t('lobby.resume')}</span>
          <button type="button" className="btn btn--primary btn--sm" onClick={() => goToMatch(lobby.my_active_match!)}>
            {t('lobby.resumeAction')}
          </button>
        </div>
      ) : null}

      {/*
        ★★ C02 §5 v1.7：對手按了再戰，正在等我 —— **在大廳也要看得見**。

        它排在最上面（跟「你有一場進行中的對局」同一區）是因為它有時限：
        120 秒之後那把鑰匙就死了，而玩家在大廳做的每一件事都比它慢。

        導頁**由玩家自己按**，不自動跳：這則事件的 `match_id` 是**原對局**的 id，
        自動跳等於把他丟回一場已經結束的對局。他按下去回到結束畫面，
        那裡才有「接受，再來一場」那顆按鈕（`MatchPage`）。
      */}
      {liveRematchInvite ? (
        <div className="notice notice--warn spread" role="status" aria-live="polite">
          <span>
            <strong>
              {t('lobby.rematchInvite', { name: liveRematchInvite.fromDisplayName || t('match.opponent') })}
            </strong>{' '}
            <span className="faint">
              {rematchInviteSeconds === null
                ? t('lobby.rematchInvite.hint')
                : t('lobby.rematchInvite.expiresIn', { seconds: rematchInviteSeconds })}
            </span>
          </span>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => goToMatch(liveRematchInvite.matchId)}
          >
            {t('lobby.rematchInvite.action')}
          </button>
        </div>
      ) : null}

      <section className="stack stack--tight">
        <div className="spread">
          <h1 className="h1">{t('lobby.title')}</h1>
          <span className="badge">
            {t('lobby.online')} {lobby?.online_count ?? '—'}
          </span>
        </div>
        <p className="faint">{t('lobby.guestNotice')}</p>
      </section>

      {/* ★ C02 §4.2：被挑戰的人必須看得見挑戰。
          後端 `_push_challenge()` 一直都把它寫進 Redis 並回在大廳回應裡，
          前端零消費 ⇒ 第二張票永遠不存在 ⇒ 指名對決一場也配不出來。 */}
      {challenges.length > 0 ? (
        <section className="stack stack--tight" aria-label={t('lobby.challenges')}>
          <h2 className="h2">{t('lobby.challenges')}</h2>
          <p className="faint">{t('lobby.challenges.hint')}</p>
          {challenges.map((c) => (
            <div className="notice spread" key={`${c.from_subject_id}:${c.variant_key}`}>
              <span>
                <strong>{c.from_display_name || c.from_subject_id}</strong>{' '}
                <span className="faint">
                  · {t('lobby.challenges.variant')}{' '}
                  {variantLabelKey(c.variant_key) ? t(variantLabelKey(c.variant_key)!) : c.variant_key}
                </span>
              </span>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                disabled={busy || ticketId !== null}
                onClick={() => void acceptChallenge(c)}
              >
                {t('lobby.challenges.accept')}
              </button>
            </div>
          ))}
        </section>
      ) : null}

      <section className="stack stack--tight" aria-label={t('lobby.title')}>
        <div className="variant-grid">
          {variants.map((v) => {
            const labelKey = variantLabelKey(v.variant_key);
            const q = queuedFor(v);
            return (
              <button
                key={v.variant_key}
                type="button"
                className="variant-card"
                aria-pressed={variantKey === v.variant_key}
                onClick={() => setVariantKey(v.variant_key)}
              >
                <strong className="h3">{labelKey ? t(labelKey) : v.display_name}</strong>
                <span className="faint">
                  {v.config?.rounds_to_win ?? '?'} 勝制 · {(v.config?.move_timeout_ms ?? 10000) / 1000}s / 回合
                </span>
                <span className="faint">
                  {t('lobby.queued')} {q?.queued ?? 0} · {t('lobby.openTables')} {q?.open_tables ?? 0} ·{' '}
                  {t('lobby.online')} {v.online_players ?? 0}
                </span>
              </button>
            );
          })}
          {variants.length === 0 ? <p className="muted">{t('common.loading')}</p> : null}
        </div>
      </section>

      <section className="stack stack--tight" aria-label={t('lobby.mode.title')}>
        <h2 className="h2">{t('lobby.mode.title')}</h2>
        <div className="mode-list">
          {MODES.map((m) => (
            <button
              key={m}
              type="button"
              className="mode-card"
              aria-pressed={mode === m}
              /* ★ C02 §4.1：名單為空（沒載入／伺服器沒開放）時 vs_ai **不可選** ——
                 給一顆按下去必然 422 的按鈕比不給更糟。 */
              disabled={m === 'vs_ai' && !vsAiAvailable}
              onClick={() => setMode(m)}
            >
              <strong>{t(`lobby.mode.${m}` as StringKey)}</strong>
              <span className="faint">{t(`lobby.mode.${m}.hint` as StringKey)}</span>
            </button>
          ))}
        </div>

        {mode === 'vs_ai' ? (
          <div className="field">
            <label htmlFor="ai-profile">{t('lobby.aiProfile')}</label>
            {/* ★ 清單來自伺服器（C02 §4.1）。這裡**沒有**任何寫死的 profile 名字：
                前端猜一個名字送上去，就是修復前那個 100% 422 的缺陷。 */}
            {vsAiAvailable ? (
              <select
                id="ai-profile"
                className="input"
                value={aiProfile ?? ''}
                onChange={(e) => setAiProfile(e.target.value)}
              >
                {aiProfiles.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.display_name || p.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="faint">{t('lobby.aiProfile.unavailable')}</span>
            )}
          </div>
        ) : null}

        {mode === 'direct' ? (
          <div className="field">
            <label htmlFor="opponent-id">{t('lobby.opponentId')}</label>
            <input
              id="opponent-id"
              className="input"
              value={opponentId}
              onChange={(e) => setOpponentId(e.target.value)}
              placeholder="player:01J…"
            />
            {/* C03 §2：guest 不能被指名對決 —— 後端會擋，這裡先提醒。 */}
            <span className="faint">指名對決需要雙方都是登入玩家（C03 §2）。</span>
          </div>
        ) : null}
      </section>

      {error ? <div className="notice notice--error">{error}</div> : null}
      {notice ? (
        <div className="notice spread">
          <span aria-live="polite">{t(notice)}</span>
          <button type="button" className="btn btn--sm" onClick={() => setNotice(null)}>
            {t('error.dismiss')}
          </button>
        </div>
      ) : null}

      <section className="stack stack--tight">
        {ticketId ? (
          <div className="spread">
            <span aria-live="polite">
              {t('lobby.queueing')} · {t('lobby.queuePosition', { seconds: elapsed })}
            </span>
            <button type="button" className="btn btn--sm" onClick={() => void cancelQueue()}>
              {t('lobby.cancel')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--block"
            disabled={
              !variantKey ||
              busy ||
              (mode === 'direct' && !opponentId.trim()) ||
              (mode === 'vs_ai' && !aiProfile)
            }
            onClick={() => void startQueue()}
          >
            {mode === 'invite' ? t('invite.create') : t('lobby.start')}
          </button>
        )}

        {invite ? (
          <div className="card card--flat stack stack--tight">
            <div className="spread">
              <span className="faint">{t('invite.code')}</span>
              <strong className="h2">{invite.invite_code}</strong>
            </div>
            {/* C03 §5：分享連結用編譯期的 PUBLIC_BASE_URL，不用 window.location。
                後端也會回一個 join_url；兩者應相同，我們顯示後端那份以免不一致。
                ★ C02 §4：後端沒設定大廳 base URL 時 join_url 是 **null**（不編造）——
                  那條路徑要顯示邀請碼讓人手動輸入，不是把 "null" 貼給朋友。 */}
            {invite.join_url ? (
              <>
                <span className="faint">{t('invite.link')}</span>
                <code className="mono">{invite.join_url}</code>
              </>
            ) : (
              <span className="faint">{t('invite.linkUnavailable')}</span>
            )}
            <div className="row">
              {invite.join_url ? (
                <button
                  type="button"
                  className="btn btn--sm"
                  onClick={() => {
                    void navigator.clipboard?.writeText(invite.join_url!).then(() => setCopied(true));
                  }}
                >
                  {copied ? t('invite.copied') : t('invite.copy')}
                </button>
              ) : null}
              <span className="faint">{t('invite.waiting')}</span>
            </div>
          </div>
        ) : null}
      </section>

      <section className="stack stack--tight">
        <h2 className="h2">{t('invite.join')}</h2>
        <div className="row">
          <input
            className="input"
            style={{ maxWidth: '14rem' }}
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            placeholder={t('invite.joinPlaceholder')}
            aria-label={t('invite.join')}
            maxLength={12}
          />
          <button type="button" className="btn" disabled={busy || !joinCode.trim()} onClick={() => void joinByCode()}>
            {busy ? t('invite.joining') : t('invite.join')}
          </button>
        </div>
      </section>
    </div>
  );
}

export function describeError(e: unknown, t: (k: StringKey) => string): string {
  if (e instanceof ApiError) {
    if (e.status === 429) return t('error.rateLimited');
    if (e.status === 404) return t('error.notFound');
    if (e.code === 'NETWORK_ERROR') return t('error.network');
    return `${e.code}: ${e.message}`;
  }
  return t('error.generic');
}
